import apiClient from "./client";
import type { Artifact } from "@/types/api";

export async function listArtifacts(projectId: string): Promise<Artifact[]> {
  const res = await apiClient.get<Artifact[]>(
    `/api/projects/${projectId}/artifacts`,
  );
  return res.data;
}

export async function getArtifactCount(projectId: string): Promise<number> {
  const res = await apiClient.get<{ count: number }>(
    `/api/projects/${projectId}/artifacts/count`,
  );
  return res.data.count;
}

/** Extrai o nome de arquivo de um header `Content-Disposition`.
 *
 * Prefere `filename*=UTF-8''...` (RFC 5987) — o único jeito de o backend
 * carregar um nome com acento (ex.: "Extração de configuração ERP") num
 * header HTTP, que só aceita latin-1. Cai para `filename="..."` (o
 * fallback ASCII) se o primeiro não estiver presente ou vier malformado.
 */
export function parseContentDispositionFilename(header: string | undefined | null): string | null {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // cai para o fallback ASCII abaixo
    }
  }
  const asciiMatch = header.match(/filename="([^"]+)"/i);
  return asciiMatch ? asciiMatch[1] : null;
}

export type DownloadFormat = "json" | "csv" | "xlsx" | "md";

export async function downloadArtifact(
  projectId: string,
  artifactId: string,
  format: DownloadFormat = "json",
): Promise<void> {
  const res = await apiClient.get<Blob>(
    `/api/projects/${projectId}/artifacts/${artifactId}/download`,
    { responseType: "blob", params: { format } },
  );
  const filename =
    parseContentDispositionFilename(res.headers["content-disposition"]) ??
    `${artifactId}.${format}`;
  const blob = new Blob([res.data], {
    type: res.headers["content-type"] || "application/octet-stream",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
